// Centralised sample-CSV definitions + downloader.
// One source of truth so every CSV upload spot offers a consistent template.

const CSV_TEMPLATES = {
  contacts: {
    filename: "walytic-contacts-sample.csv",
    headers: ["phone", "firstName", "lastName", "email", "companyName", "tags"],
    rows: [
      ["918367777110", "Aarav", "Sharma", "aarav@example.com", "Acme Pvt Ltd", "lead,vip"],
      ["919876543210", "Priya", "Kumar", "priya@example.com", "Globex", "newsletter"],
      ["14155552671", "John", "Doe", "john@example.com", "Initech", "trial"],
    ],
  },
  numbers: {
    filename: "walytic-numbers-sample.csv",
    headers: ["phone"],
    rows: [["918367777110"], ["919876543210"], ["14155552671"]],
  },
  bulk: {
    filename: "walytic-bulk-sample.csv",
    headers: [
      "To",
      "Message",
      "Image/File Url (Optional)",
      "whatsapp_client_id",
      "Schedule Time (Optional)",
    ],
    rows: [
      ["918367777110", "Hi {{firstName}}, welcome to Walytic!", "", 0, ""],
      ["919876543210", "Reminder: your trial ends tomorrow.", "", 0, "2026-05-01T10:00:00+05:30"],
      ["Family Group", "Happy holidays everyone!", "https://example.com/banner.jpg", 1, ""],
    ],
  },
};

function toCsvString(headers, rows) {
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers, ...rows].map((r) => r.map(escape).join(",")).join("\n");
}

// Phone-like column names that must be force-formatted as Text in Excel
// (otherwise Excel mangles long digits into scientific notation on open).
const PHONE_LIKE_HEADERS = new Set(["phone", "to", "number", "mobile", "whatsapp"]);

function isPhoneLikeHeader(h) {
  return PHONE_LIKE_HEADERS.has(String(h || "").trim().toLowerCase());
}

// Recover digits from Excel's scientific notation export. e.g.
//   "9.18367777110E+11" → "918367777110"
//   "9.18368E+11"        → "918368000000" (precision lost but at least readable)
// Returns the original string if it's not in scientific form.
export function normalizePhoneFromImport(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  if (!/[eE][+-]?\d/.test(s)) return s;
  const n = Number(s);
  if (!Number.isFinite(n) || Number.isNaN(n)) return s;
  // toLocaleString('fullwide', useGrouping:false) prints integers without
  // exponent and without thousand separators across all locales.
  return n.toLocaleString("fullwide", { useGrouping: false, maximumFractionDigits: 0 });
}

// Plain CSV download. We default to the XLSX downloader for any template
// containing a phone-like column — CSV cannot prevent Excel from mangling
// long numbers on open.
export function downloadSampleCsv(kind = "contacts") {
  const t = CSV_TEMPLATES[kind] || CSV_TEMPLATES.contacts;
  if (t.headers.some(isPhoneLikeHeader)) {
    return downloadSampleXlsx(kind);
  }
  const csv = toCsvString(t.headers, t.rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = t.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// XLSX download with phone-like columns explicitly formatted as Text so
// Excel keeps the full digit string instead of converting to 9.18E+11.
export async function downloadSampleXlsx(kind = "contacts") {
  const t = CSV_TEMPLATES[kind] || CSV_TEMPLATES.contacts;
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Sample");

  ws.addRow(t.headers);
  // Format phone-like columns as Text (numFmt '@') across the whole column,
  // including the data rows we're about to add.
  t.headers.forEach((h, i) => {
    if (isPhoneLikeHeader(h)) {
      const col = ws.getColumn(i + 1);
      col.numFmt = "@";
    }
  });
  for (const row of t.rows) {
    // Wrap phone-like values as strings so ExcelJS stores text, not number.
    const cells = row.map((v, i) => (isPhoneLikeHeader(t.headers[i]) ? String(v ?? "") : v));
    ws.addRow(cells);
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = t.filename.replace(/\.csv$/i, ".xlsx");
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const CSV_SAMPLE_KINDS = Object.keys(CSV_TEMPLATES);
